import { Box, Tooltip, Typography } from "@mui/material";
import DnsIcon from "@mui/icons-material/Dns";
import BugReportIcon from "@mui/icons-material/BugReport";
import HubIcon from "@mui/icons-material/Hub";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useMemo } from "react";
import { colors } from "../../../../theme";
import {
  vulnSeverityTier, severityStyleForTier, severityIconForTier,
  allVulnerabilitiesForAsset, riskScoreStyle,
} from "./assetUtils";

const OVERVIEW_ACCENT = "#459095";

// Reads whichever of the two per-asset risk scores exists, preferring the live one —
// mirrors the same fallback used everywhere else (grid column, security score).
const effectiveRiskScore = (row) =>
  typeof row.dynamic_risk_score === "number" ? row.dynamic_risk_score
  : (typeof row.risk_score === "number" ? row.risk_score : null);

const Tile = ({ icon: Icon, label, value, color = colors.grey[100], hint }) => {
  const tile = (
    <Box
      sx={{
        flex: "1 1 140px",
        minWidth: 140,
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1.2,
        borderRadius: 1,
        backgroundColor: "#1c1c1c",
        border: `1px solid ${colors.grey[800]}`,
      }}
    >
      {Icon && <Icon sx={{ color, fontSize: 26, flexShrink: 0 }} />}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: colors.grey[400], textTransform: "uppercase", letterSpacing: 0.6, whiteSpace: "nowrap" }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1.2 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
  return hint ? <Tooltip title={hint}>{tile}</Tooltip> : tile;
};

const SEVERITY_TIERS = [
  { key: "critical", label: "Critical" },
  { key: "high",     label: "High" },
  { key: "medium",   label: "Medium" },
  { key: "low",      label: "Low" },
];

// Stat-tile row above the Inventory grid — a stable overview of the whole loaded
// inventory (not affected by the toolbar's column filters).
const AssetsKpiBar = ({ rows }) => {
  const kpis = useMemo(() => {
    const bySeverity = { critical: 0, high: 0, medium: 0, low: 0 };
    let withVuln = 0;
    let criticalRisk = 0;
    let highRisk = 0;
    let gateways = 0;

    (rows || []).forEach((row) => {
      const vulns = allVulnerabilitiesForAsset(row);
      if (vulns.length > 0) withVuln++;
      vulns.forEach((v) => {
        const tier = vulnSeverityTier(v);
        if (tier !== "none") bySeverity[tier]++;
      });
      const score = effectiveRiskScore(row);
      if (score !== null) {
        const band = riskScoreStyle(score).label;
        if (band === "Critical") criticalRisk++;
        else if (band === "High") highRisk++;
      }
      if (row.is_gateway === true) gateways++;
    });

    return { total: (rows || []).length, withVuln, criticalRisk, highRisk, gateways, bySeverity };
  }, [rows]);

  const criticalRiskColor = riskScoreStyle(90).color; // any score in the Critical band gives the band's colour
  const highRiskColor = riskScoreStyle(70).color;      // any score in the High band gives the band's colour

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
      <Tile icon={DnsIcon} label="Total Assets" value={kpis.total} color={OVERVIEW_ACCENT} />
      <Tile
        icon={LocalFireDepartmentIcon}
        label="Critical Risk"
        value={kpis.criticalRisk}
        color={kpis.criticalRisk > 0 ? criticalRiskColor : colors.grey[500]}
        hint="Assets whose own risk score falls in the Critical band — the top risk tier the probe assigns, not a CVE count."
      />
      <Tile
        icon={WarningAmberIcon}
        label="High Risk"
        value={kpis.highRisk}
        color={kpis.highRisk > 0 ? highRiskColor : colors.grey[500]}
        hint="Assets whose own risk score falls in the High band — one tier below Critical."
      />
      <Tile
        icon={HubIcon}
        label="Gateways"
        value={kpis.gateways}
        color={kpis.gateways > 0 ? "#ffb422" : colors.grey[500]}
        hint="Assets that route for others — the traffic attributed to them is not necessarily their own."
      />
      <Tile icon={BugReportIcon} label="W/ CVEs" value={kpis.withVuln} color={OVERVIEW_ACCENT} />
      {SEVERITY_TIERS.map(({ key, label }) => {
        const c = severityStyleForTier(key);
        const Icon = severityIconForTier(key);
        return (
          <Tile
            key={key}
            icon={Icon}
            label={`${label} CVEs`}
            value={kpis.bySeverity[key]}
            color={kpis.bySeverity[key] > 0 ? c.color : colors.grey[500]}
          />
        );
      })}
    </Box>
  );
};

export default AssetsKpiBar;
